import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createPostServiceStudent,
  updatePostServiceStudent,
} from "../../../services/postService.services";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudents } from "../../../state/reducers/postServiceStudentSlice";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { v4 as uuidv4 } from "uuid";

function CreatePostService({
  setIsModalOpen,
  selectedStudent,
  selectedFields,
  edit,
  setEdit,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [selectedFieldsState, setSelectedFieldsState] = useState(
    () => selectedFields || []
  );
  const [isPageCreated, setIsPageCreated] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Access the state from Redux store
  const { students } = useSelector((state) => state.postServiceStudents);
  const careerFields = useSelector((state) => state.manageDetails.careerFields);

  // Generate a random UUID link
  const generateUUIDLink = () =>
    `${process.env.REACT_APP_BASE_URL}/profile-building/${uuidv4()}`;

  // Function to generate a random password
  const generateRandomPassword = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    return Array.from({ length: 12 }, () =>
      characters.charAt(Math.floor(Math.random() * characters.length))
    ).join("");
  };

  // State for storing the generated UUID link and password
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState(
    generateRandomPassword()
  );

  // Check if student already exists
  const isDuplicateStudent = (student) => {
    return students.some(
      (existingStudent) => existingStudent.customer_id === student.id
    );
  };

  // Function to copy login details
  const handleCopyDetails = () => {
    const details = `
Link: ${generatedLink}
Email ID: ${selectedStudent?.email || selectedStudent.customerEmail}
Password: ${selectedStudent?.plain_password || generatedPassword}`;

    navigator.clipboard
      .writeText(details)
      .then(() => {
        setIsCopied(true);
        toast.success("Login details copied to clipboard!");
        setTimeout(() => setIsCopied(false), 3000);
      })
      .catch((err) => {
        toast.error("Failed to copy details");
        console.error("Copy failed: ", err);
      });
  };

  // Format career field options for the Select component
  const careerFieldOptions = (careerFields || []).map((field) => ({
    value: field.id,
    label: field.domain_name,
  }));

  // Load existing data when in edit mode
  useEffect(() => {
    if (edit && selectedStudent && careerFields.length > 0) {
      // Map selectedStudent's existing career_fields to the formatted options
      const matchedFields = selectedStudent?.career_fields?.map((field) => ({
        value: field.id,
        label: field.domain_name,
      }));
      setSelectedFieldsState(matchedFields);

      setGeneratedLink(
        `${process.env.REACT_APP_CURRENT_URL}/profile-building/${
          selectedStudent.customer_uuid || selectedStudent.uuid
        }`
      );

      setGeneratedPassword(
        selectedStudent?.plain_password || generateRandomPassword()
      );
    }
  }, [edit, selectedStudent, careerFields]);

  // Handle create or update action
  const handleCreateOrUpdate = async () => {
    if (!selectedStudent || !selectedStudent.id) {
      toast.error("No student selected!");
      return;
    }

    if (
      !Array.isArray(selectedFieldsState) ||
      selectedFieldsState.length === 0
    ) {
      toast.error("Please select at least one career field.");
      return;
    }

    setLoading(true);
    const studentData = {
      encrypted_id: selectedStudent.customer_uuid,
      customer_id: selectedStudent.id,
      password: generatedPassword,
      career_fields: (selectedFieldsState || []).map((field) => ({
        id: field.value,
        domain_name: field.label,
      })),
      user_id: selectedStudent.id,
    };

    try {
      const response = edit
        ? await updatePostServiceStudent(studentData)
        : await createPostServiceStudent(studentData);

      toast.success(
        `Post Service Page ${edit ? "updated" : "created"} successfully!`
      );
      setIsPageCreated(true);
      dispatch(fetchStudents());

      // Don't close modal immediately on creation to allow copying credentials
      if (edit) {
        setTimeout(() => {
          setEdit(false);
          setIsModalOpen(false);
        }, 1500);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(
        `Failed to ${edit ? "update" : "create"} post service student.`
      );
    } finally {
      setLoading(false);
    }
  };

  // Custom styles for React-Select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "0.5rem",
      borderColor: "#E2E8F0",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#3B82F6",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#EFF6FF",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#2563EB",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#2563EB",
      "&:hover": {
        backgroundColor: "#DBEAFE",
        color: "#1E40AF",
      },
    }),
  };

  return (
    <div className="h-full overflow-y-auto w-full max-w-7xl">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {edit ? "Edit" : "Create"} Career Guidance Page
        </h1>
        {/* <p className="text-gray-600 mt-1">
          {edit
            ? "Update the career guidance page for this student"
            : "Set up a new career guidance page with custom credentials"}
        </p> */}
      </div>

      <div className="space-y-6">
        {/* Students Details Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-2 rounded-full mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-blue-600"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-800">
              Student Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Student Name</div>
                <div className="font-medium">
                  {selectedStudent?.name ||
                    selectedStudent.customerName ||
                    "N/A"}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Email Address</div>
                <div className="font-medium">
                  {selectedStudent?.email ||
                    selectedStudent.customerEmail ||
                    "N/A"}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Mobile Number</div>
                <div className="font-medium">
                  {selectedStudent?.phone ||
                    selectedStudent.customerPhone ||
                    "N/A"}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">
                  {edit ? "Update Career Fields" : "Selected Career Fields"}
                </div>

                {!edit ? (
                  <div className="font-medium">
                    {selectedFieldsState.length > 0
                      ? selectedFieldsState.map((field, index) => (
                          <span
                            key={field.value}
                            className="inline-block bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full mr-2 mb-2"
                          >
                            {field.label}
                          </span>
                        ))
                      : "No fields selected"}
                  </div>
                ) : (
                  <Select
                    isMulti
                    options={careerFieldOptions}
                    value={selectedFieldsState}
                    onChange={(selected) => {
                      setSelectedFieldsState(selected);
                    }}
                    placeholder="Select career fields..."
                    styles={customSelectStyles}
                    className="mt-1 z-10"
                  />
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">SWOT Report</div>

                {selectedStudent?.swot_test_link ||
                selectedStudent?.customer?.swot_test_link ? (
                  <div className="overflow-hidden text-ellipsis">
                    <a
                      href={`${process.env.REACT_APP_BASE_URL}${
                        selectedStudent?.swot_test_link ||
                        selectedStudent?.customer?.swot_test_link
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                      </svg>
                      View SWOT Report
                    </a>
                  </div>
                ) : (
                  <span className="text-gray-500 font-medium">
                    Not available
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Login Credentials Section */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-blue-200 p-2 rounded-full mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-blue-700"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-800">
              Login Credentials
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-blue-100 flex flex-col sm:flex-row sm:items-center justify-between">
              <div className="mb-3 sm:mb-0">
                <div className="text-sm text-gray-500 mb-1">Access Link</div>
                <div className="font-medium text-blue-800 break-all">
                  <a
                    href={`${
                      process.env.REACT_APP_CURRENT_URL
                    }/profile-building/${
                      selectedStudent.customer_uuid || selectedStudent.uuid
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {`${process.env.REACT_APP_CURRENT_URL}/profile-building/${
                      selectedStudent.customer_uuid || selectedStudent.uuid
                    }`}
                  </a>
                </div>
              </div>
              {/* <button
                onClick={() => setGeneratedLink(generateUUIDLink())}
                className="bg-blue-100 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-200 transition duration-200 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
                Generate New Link
              </button> */}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-blue-100">
                <div className="text-sm text-gray-500 mb-1">Email Address</div>
                <div className="font-medium text-blue-800">
                  {selectedStudent?.email ||
                    selectedStudent.customerEmail ||
                    "N/A"}
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-blue-100 flex flex-col justify-between">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Password</div>
                  <div className="font-mono font-medium text-blue-800 break-all">
                    {selectedStudent.plain_password || generatedPassword}
                  </div>
                </div>
                <button
                  onClick={() => setGeneratedPassword(generateRandomPassword())}
                  className="bg-blue-100 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-200 transition duration-200 mt-3 flex items-center justify-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Generate New Password
                </button>
              </div>
            </div>

            {/* Copy All Details Button */}
            <div className="mt-4">
              <button
                onClick={handleCopyDetails}
                className={`w-full py-3 px-4 rounded-lg flex items-center justify-center transition duration-200 ${
                  isCopied
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isCopied ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M8 2a1 1 0 000 2h2a1 1 0 100-2H8z" />
                      <path d="M3 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v6h-4.586l1.293-1.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L10.414 13H15v3a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM15 11h2a1 1 0 110 2h-2v-2z" />
                    </svg>
                    Copy All Login Details
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
          <button
            onClick={() => {
              setIsModalOpen(false);
              setEdit(false);
            }}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition duration-200 flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Cancel
          </button>

          <button
            onClick={handleCreateOrUpdate}
            disabled={loading}
            className={`px-6 py-3 rounded-lg transition duration-200 flex items-center justify-center ${
              loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : edit
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {edit ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  {edit ? (
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  )}
                </svg>
                {edit ? "Update Page" : "Create Page"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreatePostService;
